/**
 * Réessaie une opération réseau, mais uniquement pendant le build (SSG).
 *
 * Pourquoi c'est nécessaire : le workflow construit et pousse d'abord l'image
 * backend, puis construit le frontend — lequel interroge l'API DÉJÀ EN LIGNE
 * pour pré-rendre /services et /faq. Or Watchtower ne récupère la nouvelle
 * image backend qu'à son prochain passage (--interval 60), puis redémarre le
 * conteneur. Pendant cette fenêtre, l'API est soit l'ancienne version (une
 * route ajoutée dans la même livraison répond alors 404), soit indisponible le
 * temps du redémarrage. Sans attente suffisante, le pré-rendu produirait des
 * pages vides et l'assertion post-build interromprait la livraison.
 *
 * Le budget (~3 min) couvre donc le passage de Watchtower et le redémarrage du
 * backend, migrations comprises.
 *
 * Côté navigateur, aucune tentative supplémentaire : une seule requête, pour
 * ne pas bloquer la navigation vers la page si l'API ne répond pas. L'appelant
 * ignore alors l'échec et conserve les données figées au build.
 */
const BUILD_ATTEMPTS = 12;
const BUILD_DELAY_MS = 15000;

export async function withRetry<T>(
  operation: () => Promise<T>,
  attempts: number = import.meta.env.SSR ? BUILD_ATTEMPTS : 1,
  delayMs: number = BUILD_DELAY_MS,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < attempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  throw lastError;
}
